import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Search, Filter, Plus, Eye, Download, Check, X,
  Building, Key, Server, Cog, Globe, Code, Handshake, AlertTriangle,
  RefreshCw, ClipboardCheck, Users, Shield, Database, Lock, TrendingUp,
  ChevronRight, Clock, User, Calendar, Loader2, Package
} from 'lucide-react';
import {
  getPolicyCategories,
  getPolicyTemplates,
  getGeneratedPolicies,
  getPolicyDashboard,
  getCategoryIcon,
  getStatusColor,
  getStatusLabel,
  getFrameworkBadgeColor,
  getPolicyTemplate
} from '../../services/policyTemplates';
import { getPackFrameworks, generatePolicyPack } from '../../services/grc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';

const categoryIcons = {
  governance: Building,
  access_control: Key,
  asset_management: Server,
  operations: Cog,
  communications: Globe,
  development: Code,
  supplier: Handshake,
  incident: AlertTriangle,
  continuity: RefreshCw,
  compliance: ClipboardCheck,
  hr: Users,
  physical: Shield,
  data: Database,
  cryptography: Lock,
  risk: TrendingUp
};

export default function PolicyTemplatesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('templates');
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [generatedPolicies, setGeneratedPolicies] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFramework, setSelectedFramework] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPackDialog, setShowPackDialog] = useState(false);
  const [packFrameworks, setPackFrameworks] = useState([]);
  const [packForm, setPackForm] = useState({ framework: '', approver_role: '', org_name: '' });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadData();
    loadPackFrameworks();
  }, []);

  const loadPackFrameworks = async () => {
    try {
      const data = await getPackFrameworks();
      setPackFrameworks(data.frameworks || []);
    } catch {}
  };

  const handleGeneratePack = async () => {
    if (!packForm.framework) return toast.error('Select a framework');
    try {
      setGenerating(true);
      const result = await generatePolicyPack(packForm);
      toast.success(`Generated ${result.generated} policies for ${result.framework} (${result.skipped} already exist)`);
      setShowPackDialog(false);
      setPackForm({ framework: '', approver_role: '', org_name: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'templates') {
      loadTemplates();
    } else if (activeTab === 'generated') {
      loadGeneratedPolicies();
    }
  }, [activeTab, selectedCategory, selectedFramework, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, dashboardData] = await Promise.all([
        getPolicyCategories(),
        getPolicyDashboard()
      ]);
      setCategories(categoriesData);
      setDashboard(dashboardData);
      await loadTemplates();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (selectedFramework) params.framework = selectedFramework;
      if (searchTerm) params.search = searchTerm;
      const data = await getPolicyTemplates(params);
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadGeneratedPolicies = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      const data = await getGeneratedPolicies(params);
      setGeneratedPolicies(data);
    } catch (error) {
      console.error('Error loading generated policies:', error);
    }
  };

  const handlePreviewTemplate = async (e, template) => {
    e.stopPropagation(); // Prevent card click navigation
    setPreviewLoading(template.id);
    try {
      const fullTemplate = await getPolicyTemplate(template.id);
      setPreviewTemplate(fullTemplate);
    } catch (error) {
      console.error('Error loading template preview:', error);
    }
    setPreviewLoading(false);
  };

  const frameworks = ['ISO27001', 'NIST', 'SOC2', 'GDPR', 'HIPAA', 'PCI-DSS', 'CIS'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-8 h-8 text-blue-600" />
              Policy Templates
            </h1>
            <p className="text-gray-600 mt-1">
              Create and manage organizational policies from industry-standard templates
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowPackDialog(true)} variant="outline" data-testid="generate-pack-btn">
              <Package className="w-4 h-4 mr-2" /> Generate Pack
            </Button>
            <button
              onClick={() => navigate('/grc/policy-templates/create')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Policy
            </button>
          </div>
        </div>

        {/* Dashboard Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="text-sm text-gray-500">Available Templates</div>
              <div className="text-2xl font-bold text-blue-600">{dashboard.total_templates}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="text-sm text-gray-500">Generated Policies</div>
              <div className="text-2xl font-bold text-green-600">{dashboard.total_generated_policies}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="text-sm text-gray-500">Pending Review</div>
              <div className="text-2xl font-bold text-yellow-600">{dashboard.pending_review}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="text-sm text-gray-500">Approved</div>
              <div className="text-2xl font-bold text-emerald-600">{dashboard.approved_policies}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 border-b">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'templates'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Policy Templates ({templates.length})
          </button>
          <button
            onClick={() => setActiveTab('generated')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'generated'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Policies ({generatedPolicies.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'categories'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Categories ({categories.length})
          </button>
        </div>
      </div>

      {/* Filters */}
      {activeTab === 'templates' && (
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <select
            value={selectedFramework}
            onChange={(e) => setSelectedFramework(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Frameworks</option>
            {frameworks.map(fw => (
              <option key={fw} value={fw}>{fw}</option>
            ))}
          </select>
        </div>
      )}

      {/* Templates Grid */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => {
            const IconComponent = categoryIcons[template.category] || FileText;
            return (
              <div
                key={template.id}
                className="bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                data-testid={`policy-template-${template.id}`}
              >
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <IconComponent className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                      <p className="text-sm text-gray-500">v{template.version}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.description}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {template.frameworks.slice(0, 3).map(fw => (
                      <span
                        key={fw}
                        className={`px-2 py-0.5 text-xs font-medium rounded bg-${getFrameworkBadgeColor(fw)}-100 text-${getFrameworkBadgeColor(fw)}-700`}
                      >
                        {fw}
                      </span>
                    ))}
                    {template.frameworks.length > 3 && (
                      <span className="px-2 py-0.5 text-xs text-gray-500">
                        +{template.frameworks.length - 3} more
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>{template.section_count} sections</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => handlePreviewTemplate(e, template)}
                      disabled={previewLoading === template.id}
                      data-testid={`preview-template-${template.id}`}
                    >
                      {previewLoading === template.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-1" />
                      )}
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/grc/policy-templates/create?template=${template.id}`)}
                      data-testid={`use-template-${template.id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Use Template
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Generated Policies List */}
      {activeTab === 'generated' && (
        <div className="space-y-4">
          {generatedPolicies.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No policies created yet</h3>
              <p className="text-gray-500 mb-4">Start by selecting a template and creating your first policy</p>
              <button
                onClick={() => setActiveTab('templates')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Browse Templates
              </button>
            </div>
          ) : (
            generatedPolicies.map(policy => (
              <div
                key={policy.id}
                className="bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer p-4"
                onClick={() => navigate(`/grc/policy-templates/view/${policy.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{policy.title}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${getStatusColor(policy.status)}-100 text-${getStatusColor(policy.status)}-700`}>
                        {getStatusLabel(policy.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">Based on: {policy.template_name}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {policy.owner}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(policy.created_at).toLocaleDateString()}
                      </span>
                      {policy.control_count > 0 && (
                        <span className="flex items-center gap-1">
                          <ClipboardCheck className="w-4 h-4" />
                          {policy.control_count} controls linked
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Categories Grid */}
      {activeTab === 'categories' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map(cat => {
            const IconComponent = categoryIcons[cat.id] || FileText;
            return (
              <div
                key={cat.id}
                className="bg-white rounded-lg border shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setActiveTab('templates');
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <IconComponent className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                    <p className="text-sm text-gray-500">{cat.template_count} templates</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">{cat.description}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Template Preview Modal */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              {previewTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Version {previewTemplate?.version} • {previewTemplate?.section_count} sections
            </DialogDescription>
          </DialogHeader>
          
          {previewTemplate && (
            <div className="flex-1 overflow-hidden">
              {/* Framework badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {previewTemplate.frameworks?.map(fw => (
                  <Badge key={fw} variant="outline">{fw}</Badge>
                ))}
              </div>
              
              <p className="text-sm text-gray-600 mb-4">{previewTemplate.description}</p>
              
              {/* Sections preview */}
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-4">
                  {previewTemplate.sections?.map((section, idx) => (
                    <div key={section.id || idx} className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {idx + 1}. {section.title}
                      </h4>
                      {section.description && (
                        <p className="text-sm text-gray-600 mb-3">{section.description}</p>
                      )}
                      {section.content && (
                        <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-3 border">
                          {section.content.length > 500 
                            ? section.content.substring(0, 500) + '...' 
                            : section.content}
                        </div>
                      )}
                      {section.subsections && section.subsections.length > 0 && (
                        <div className="mt-3 pl-4 border-l-2 border-blue-200 space-y-2">
                          {section.subsections.map((sub, subIdx) => (
                            <div key={sub.id || subIdx}>
                              <p className="text-sm font-medium text-gray-800">
                                {idx + 1}.{subIdx + 1} {sub.title}
                              </p>
                              {sub.content && (
                                <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                  {sub.content}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
              
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
                  Close
                </Button>
                <Button onClick={() => {
                  setPreviewTemplate(null);
                  navigate(`/grc/policy-templates/create?template=${previewTemplate.id}`);
                }}>
                  <Plus className="w-4 h-4 mr-1" />
                  Use This Template
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate Pack Dialog */}
      <Dialog open={showPackDialog} onOpenChange={setShowPackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5" /> Generate Policy Pack</DialogTitle>
            <DialogDescription>Batch-generate all policy templates for a framework with pre-assigned owners and controls.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Framework</Label>
              <Select value={packForm.framework} onValueChange={v => setPackForm(p => ({ ...p, framework: v }))}>
                <SelectTrigger data-testid="pack-framework-select"><SelectValue placeholder="Select framework" /></SelectTrigger>
                <SelectContent>
                  {packFrameworks.map(f => (
                    <SelectItem key={f.framework} value={f.framework}>{f.framework} ({f.template_count} templates)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Organization Name</Label>
              <Input placeholder="e.g., Beskar IT" value={packForm.org_name} onChange={e => setPackForm(p => ({ ...p, org_name: e.target.value }))} data-testid="pack-org-input" />
            </div>
            <div>
              <Label>Approver Role (optional)</Label>
              <Input placeholder="e.g., admin, compliance_lead" value={packForm.approver_role} onChange={e => setPackForm(p => ({ ...p, approver_role: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackDialog(false)}>Cancel</Button>
            <Button onClick={handleGeneratePack} disabled={generating} data-testid="generate-pack-submit">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
              Generate Pack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
